import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;

    return {
        loaded: !!getProductListDataView(state).value,
        view: state.pages.productList.view,
        query: correctedQuery || productFilters.query,
        productsDataView: getProductListDataView(state),
    };
};

const enum fields {
    title = "title",
    image = "image",
    linkTo = "linkTo",
    icon = "icon",
}

type OwnProps = ReturnType<typeof mapStateToProps> & WidgetProps;

interface ProductListPaginationStrip {
    wrapper?: InjectableCss;
    sortContainer?: InjectableCss;
    paginationContainer?: InjectableCss;
    filterContainer?: InjectableCss;
}

export const styles: ProductListPaginationStrip = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            background: ${getColor("secondary")};
            border-radius: 6px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        null,
                        null,
                        css`
                            flex-direction: row;
                            overflow: hidden;
                            padding-left: 25px;
                            max-height: 80px;
                        `,
                        css`
                            max-height: 49px;
                            min-height: 49px;
                        `,
                    ],
                    "min",
                )};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-top: 20px;
                        `,
                    ],
                    "max",
                )};
        `,
    },
    sortContainer: {
        css: css`
            flex-grow: 0;
            [class^="FormFieldStyle"] {
                width: 100% !important;
                [class^="FormInputWrapper"] {
                    [class^="SelectStyle"] {
                        width: 115px;
                    }
                }
            }
        `,
    },
    paginationContainer: {
        css: css`
            flex-grow: 1;
        `,
    },
    filterContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            margin-left: 30px;
                        `,
                    ],
                    "min",
                )};
        `,
    },
};

const ProductListPaginationStrip: FC<OwnProps> = ({ id, view, query, productsDataView }) => {
    const products = productsDataView.value;
    if (products?.length === 0) {
        return null;
    }
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.sortContainer}>
                <Zone zoneName="sortcontent" contentId={id} />
            </StyledWrapper>
            {view === "Table" && (
                <StyledWrapper {...styles.filterContainer}>
                    <Zone zoneName="filtercontent" contentId={id} />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.paginationContainer}>
                <Zone zoneName="paginationcontent" contentId={id} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListPaginationStrip),
    definition: {
        group: "Common",
        displayName: "ProductListPaginationStrip",
    },
};

export default widgetModule;
